import axios, {
    type AxiosInstance,
    type AxiosRequestConfig,
    type AxiosResponse,
    AxiosError,
    type InternalAxiosRequestConfig,
    HttpStatusCode,
} from 'axios';
import BrowserStorageUtil from '../utils/BrowserStorageUtil.ts';
import type {ErrorResponse, QueueItem} from './type';

const isErrorResponse = (data: any): data is ErrorResponse => {
    return data && typeof data === 'object';
};

// todo
// 여기에 usemodal 넣기

const moveLoginPage = (): void => {
    console.log('로그인 페이지로 이동!');
    // BrowserStorageUtil.clearItems();
    // window.location.href = "/ip-tv";
};

const callApi = () => {
    const baseConfig: AxiosRequestConfig = {
        baseURL: import.meta.env.VITE_SWAGGER_API_URL,
        method: 'post',
        headers: {
            'Content-Type': 'application/json;charset=UTF-8',
            'Accept-Language': 'ko-KR',
        },
        timeout: 10000,
        responseType: 'json',
    };

    axios.defaults.xsrfCookieName = 'csrftoken';
    axios.defaults.xsrfHeaderName = 'X-CSRFToken';
    const $axios: AxiosInstance = axios.create(baseConfig);

    // 토큰 갱신 중인지 체크하는 플래그
    let isRefreshing = false;
    let failedQueue: QueueItem[] = [];
    const processQueue = (error: any, token: string | null = null) => {
        failedQueue.forEach(({resolve, reject}) => {
            if (error) {
                reject(error);
            } else {
                resolve(token);
            }
        });
        failedQueue = [];
    };

    const interceptorsRequest = (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
        if (config.url?.includes('/jwt/token') || config.url?.includes('/jwt/refresh')) {
            return config;
        }

        const accessToken = BrowserStorageUtil.getItem('ACCESSTOKEN_KEY');
        if (accessToken) {
            config.headers.Authorization = `Bearer ${accessToken}`;
        } else {
            moveLoginPage();
        }

        return config;
    };

    const interceptorsResponse = (response: AxiosResponse): AxiosResponse => {
        if (!response.data.isSuccess && response.data.isSuccess !== undefined) {
            // showMessageModal({
            //     title: "에러",
            //     type: "error",
            //     message: response.data.message?.description || "오류가 발생했습니다.",
            // });
        } else if (response.data.type === 'application/octet-stream') {
            response.data.data = new Blob([response.data]);
            response.data.isSuccess = response.status === 200 ? 1 : 0;
        }
        return response;
    };

    // 원본 API 재호출 함수
    const originApiCall = async (url: string, payload: any): Promise<any> => {
        // eslint-disable-next-line no-useless-catch
        try {
            const response = await $axios.post(url, payload);
            return response.data;
        } catch (error) {
            throw error;
        }
    };

    // 토큰 갱신 함수
    const newAccessToken = async (originApiConfig: any): Promise<any> => {
        try {
            const response = await $axios.post('/auth/refresh-token', {
                refreshToken: BrowserStorageUtil.getItem('REFRESHTOKEN_KEY'),
            });

            const newAccessToken = response.data.data.accessToken;
            BrowserStorageUtil.setItem('ACCESSTOKEN_KEY', newAccessToken);

            // 원본 요청 재시도
            return await originApiCall(originApiConfig.url, originApiConfig.data);
        } catch (error) {
            // 리프레시 토큰도 만료된 경우에만 로그인 페이지로 이동
            moveLoginPage();
            throw error;
        }
    };

    const interceptorsRequestException = (error: AxiosError) => {
        return Promise.reject(error);
    };

    const interceptorsResponseException = async (error: AxiosError) => {
        const originalRequest = error.config;

        // 401 Unauthorized - 토큰 만료 처리 개선
        if (error?.response?.status === HttpStatusCode.Unauthorized && originalRequest) {
            if (isRefreshing) {
                // 이미 토큰 갱신 중이면 대기열에 추가
                return new Promise((resolve, reject) => {
                    failedQueue.push({resolve, reject});
                })
                    .then(() => {
                        // 토큰 갱신 완료 후 원본 요청 재시도
                        return $axios(originalRequest);
                    })
                    .catch((err) => {
                        return Promise.reject(err);
                    });
            }

            isRefreshing = true;

            try {
                const result = await newAccessToken(originalRequest);
                processQueue(null, result);

                // 원래 요청에 새 토큰 추가하여 재시도
                const accessToken = BrowserStorageUtil.getItem('ACCESSTOKEN_KEY');
                originalRequest.headers!.Authorization = `Bearer ${accessToken}`;
                return $axios(originalRequest);
            } catch (refreshError) {
                processQueue(refreshError, null);
                return Promise.reject(refreshError);
            } finally {
                isRefreshing = false;
            }
        }

        // 404 Not Found
        // else if (error?.response?.status === HttpStatusCode.NotFound) {
        //     window.location.href = "/error";
        // }

        // 500 Internal Server Error - 타입 가드 사용
        else if (error?.response?.status === HttpStatusCode.InternalServerError) {
            try {
                const errorData = error?.response?.data;
                let errorMessage = '서버 오류가 발생했습니다.';

                if (isErrorResponse(errorData)) {
                    if (errorData.message?.description) {
                        errorMessage = errorData.message.description;
                    } else if (errorData.description) {
                        errorMessage = errorData.description;
                    }
                } else if (typeof errorData === 'string') {
                    try {
                        const parsedError = JSON.parse(errorData);
                        if (isErrorResponse(parsedError)) {
                            errorMessage = parsedError.description || parsedError.message?.description || errorMessage;
                        } else {
                            errorMessage = errorData;
                        }
                    } catch {
                        errorMessage = errorData;
                    }
                }
                // showMessageModal({
                //     title: "에러",
                //     type: "error",
                //     message: errorMessage,
                // });
            } catch {
                // showMessageModal({
                //     title: "에러",
                //     type: "error",
                //     message: "서버 오류가 발생했습니다.",
                // });
            }
        }
        // 네트워크 오류 및 타임아웃
        else if (error.code === AxiosError.ERR_NETWORK) {
            // showMessageModal({
            //     title: "에러",
            //     type: "error",
            //     message: "네트워크 연결을 확인해주세요.",
            // });
            moveLoginPage();
        } else if (error.code === AxiosError.ECONNABORTED) {
            // showMessageModal({
            //     title: "에러",
            //     type: "error",
            //     message: "요청 시간이 초과되었습니다.",
            // });
        }
        return Promise.reject(error);
    };

    $axios.interceptors.request.use(interceptorsRequest, interceptorsRequestException);
    $axios.interceptors.response.use(interceptorsResponse, interceptorsResponseException);

    return $axios;
};

export default callApi;
